"use client";

import { useEffect } from "react";
import AppImage from "@/components/ui/AppImage";
import Icon from "@/components/ui/AppIcon";

interface Production {
  id: string;
  title: string;
  genre: string;
  year: string;
  image: string;
  alt: string;
  awards?: string;
  description?: string;
  large?: boolean;
}

export default function FeaturedProductions() {
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add("animate");
          }
        });
      },
      { threshold: 0.1, rootMargin: "0px 0px -5% 0px" }
    );

    document.querySelectorAll(".animate-on-scroll").forEach((el) => {
      observer.observe(el);
    });

    return () => observer.disconnect();
  }, []);

  const productions: Production[] = [
  {
    id: "prod_1",
    title: "Echoes of Tomorrow",
    genre: "Sci-Fi Drama",
    year: "2025",
    image: "https://img.rocket.new/generatedImages/rocket_gen_img_14a80bb99-1764989519510.png",
    alt: "Futuristic sci-fi film set with neon lighting, advanced technology props, and actors in modern costumes",
    awards: "Best Cinematography",
    description: "A thought-provoking journey through time exploring humanity's future",
    large: true
  },
  {
    id: "prod_2",
    title: "The Last Performance",
    genre: "Musical Drama",
    year: "2024",
    image: "https://images.unsplash.com/photo-1678824359076-fe858d43277e",
    alt: "Theater stage with dramatic spotlight, grand piano, and ornate curtains in burgundy and gold",
    awards: "Audience Choice Award",
    large: true
  },
  {
    id: "prod_3",
    title: "Urban Legends",
    genre: "Documentary Series",
    year: "2025",
    image: "https://images.unsplash.com/photo-1524190209076-c7eb42836a00",
    alt: "Urban cityscape at night with illuminated skyscrapers, street lights, and documentary crew filming"
  },
  {
    id: "prod_4",
    title: "Heritage Stories",
    genre: "Cultural Documentary",
    year: "2024",
    image: "https://img.rocket.new/generatedImages/rocket_gen_img_19a0dab7d-1764636256523.png",
    alt: "Historic cultural site with traditional architecture, artifacts, and documentary production equipment"
  },
  {
    id: "prod_5",
    title: "Midnight Symphony",
    genre: "Concert Film",
    year: "2024",
    image: "https://images.unsplash.com/photo-1701965668429-3f328c5b36f2",
    alt: "Symphony orchestra on stage with conductor, dramatic lighting, and full audience in concert hall"
  },
  {
    id: "prod_6",
    title: "Breaking Boundaries",
    genre: "Sports Documentary",
    year: "2025",
    image: "https://img.rocket.new/generatedImages/rocket_gen_img_17cce83b0-1764658695666.png",
    alt: "Professional athlete in action during intense training, captured with dynamic sports cinematography"
  }];


  return (
    <section
      id="portfolio"
      className="bg-background text-foreground py-24 md:py-32 relative overflow-hidden border-t border-white/5">
      
      {/* Decorative Section Number */}
      <div className="absolute top-12 left-6 md:left-12 z-0 opacity-[0.05] font-display font-bold text-[8rem] md:text-[12rem] leading-none text-white pointer-events-none select-none">
        ACT I
      </div>

      <div className="w-full max-w-7xl mx-auto px-6 md:px-12 relative z-10">
        <div className="flex flex-col md:flex-row justify-between items-start md:items-end mb-20 gap-8 animate-on-scroll [animation:animationIn_0.8s_ease-out_0.2s_both]">
          <div>
            <span className="text-xs uppercase tracking-[0.3em] text-primary font-medium mb-4 block">
              Featured Works
            </span>
            <h2 className="text-5xl md:text-7xl leading-[0.95] tracking-tighter font-display font-light text-white">
              Signature <br />
              <span className="text-muted-foreground font-display font-light">
                Productions
              </span>
            </h2>
          </div>

          <div className="flex gap-8 items-baseline border-b border-white/10 pb-2">
            <button className="text-2xl md:text-3xl text-white font-display font-light border-b-2 border-primary pb-1">
              All
            </button>
            <button className="text-2xl md:text-3xl text-muted-foreground hover:text-white transition-colors font-display font-light pb-1">
              Film
            </button>
            <button className="text-2xl md:text-3xl text-muted-foreground hover:text-white transition-colors font-display font-light pb-1">
              Events
            </button>
          </div>
        </div>

        {/* Bento Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          {productions.map((prod, index) =>
          <div
            key={prod.id}
            className={`group relative ${
            prod.large ? "md:col-span-2 lg:col-span-2" : ""} h-[520px] w-full bg-card rounded-[32px] overflow-hidden flex flex-col justify-between p-8 transition-transform duration-500 hover:-translate-y-2 hover:shadow-2xl animate-on-scroll [animation:animationIn_0.8s_ease-out_${

            0.3 + index * 0.1}s_both]`
            }>
            
              <div className="absolute top-0 right-0 p-6 opacity-40 z-20 group-hover:opacity-100 transition-opacity">
                <div className="w-12 h-12 rounded-full border border-white/20 flex items-center justify-center bg-white/5 backdrop-blur-sm text-white">
                  <Icon name="ArrowTopRightOnSquareIcon" size={24} />
                </div>
              </div>

              <div className="absolute inset-0 w-full h-full">
                <AppImage
                src={prod.image}
                alt={prod.alt}
                className="w-full h-full object-cover opacity-80 group-hover:scale-105 transition-transform duration-700" />
              
                <div className="absolute inset-0 bg-gradient-to-t from-background via-transparent to-transparent"></div>
              </div>

              <div className="mt-auto relative z-10">
                {prod.awards &&
              <div className="mb-4 inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-secondary/20 backdrop-blur-sm border border-secondary/30">
                    <Icon name="TrophyIcon" size={16} className="text-secondary" />
                    <span className="text-xs font-medium text-secondary">
                      {prod.awards}
                    </span>
                  </div>
              }
                <h3 className="text-white text-3xl tracking-tight leading-none mb-2 font-display font-light">
                  {prod.title}
                </h3>
                {prod.description &&
              <p className="text-muted-foreground text-sm mb-4 max-w-lg">
                    {prod.description}
                  </p>
              }
                <div className="flex justify-between items-center text-white/90 border-t border-white/10 pt-5">
                  <span className="text-xl font-accent italic">{prod.genre}</span>
                  <span className="text-xs tracking-wide uppercase text-muted-foreground">
                    {prod.year}
                  </span>
                </div>
              </div>
            </div>
          )}
        </div>

        <div className="mt-16 flex justify-center animate-on-scroll [animation:animationIn_0.8s_ease-out_0.6s_both]">
          <button className="group flex items-center gap-3 px-8 py-4 bg-card text-white rounded-full hover:bg-white/10 transition-all shadow-lg hover:shadow-xl border border-white/10">
            <span className="font-medium tracking-wide">View All Productions</span>
            <Icon
              name="ArrowRightIcon"
              size={20}
              className="group-hover:translate-x-1 transition-transform" />
            
          </button>
        </div>
      </div>
    </section>);

}